import { blacklistRefreshToken, signOutUrl } from './api'
import { endSessionLocally, refreshSession } from './refresh'
import {
  clearSessionCookies,
  readRefreshTokenCookie,
  setRefreshTokenCookie,
  setUserSessionCookie,
} from './session-cookies'
import { getSession } from './session-store'
import { hasEnoughLifeLeft } from './tokens'
import { ApiError, NetworkError } from '@/api/errors'

/**
 * The query parameters the backend hands a new session over in.
 *
 * Single use, and they must never survive into a url that could be sent back
 * through sign-in: a redirect carrying two sets of them cannot be read, and the
 * browser bounces between here and the identity provider indefinitely.
 */
export const SESSION_HANDOFF_PARAMS = ['token', 'user_session'] as const

export type BootstrapOutcome =
  'signed-in' | 'no-session' | 'session-rejected' | 'backend-unreachable'

/**
 * Establishes the session, once, before the router exists.
 *
 * Not a route guard. A guard runs per navigation and after the first render, so
 * the app would paint a signed-out frame and then correct itself -- and every
 * route would have to be written to tolerate a session that is still arriving.
 */
export async function bootstrapSession(
  url: URL = new URL(window.location.href),
): Promise<BootstrapOutcome> {
  const handedOverSession = url.searchParams.get('user_session')
  if (handedOverSession !== null) {
    setUserSessionCookie(handedOverSession)
  }

  const handedOverToken = url.searchParams.get('token')
  if (handedOverToken !== null) {
    setRefreshTokenCookie(handedOverToken)
  }

  stripHandoffParams(url)

  const refreshToken = handedOverToken ?? readUsableRefreshTokenCookie()
  if (refreshToken === null) {
    return 'no-session'
  }

  try {
    await refreshSession(refreshToken)
    return 'signed-in'
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // `refreshSession` has already cleared the session, but not when the token
      // came from the url and there was no session to clear.
      clearSessionCookies()
      return 'session-rejected'
    }
    if (error instanceof NetworkError) {
      // Carry on unauthenticated rather than refusing to start. The sign-in page
      // detects an unreachable backend and says so, which is the whole point of
      // it -- and blocking here would leave a white screen instead.
      return 'backend-unreachable'
    }
    throw error
  }
}

/**
 * The stored refresh token, if it is worth spending a round trip on.
 *
 * A token in its last tenth of life would refresh once and then expire, so the
 * session is over either way; going straight to sign-in is quicker and does not
 * render a shell that is about to be torn down.
 */
function readUsableRefreshTokenCookie(): string | null {
  const token = readRefreshTokenCookie()
  if (token === null) return null
  if (!hasEnoughLifeLeft(token)) {
    clearSessionCookies()
    return null
  }
  return token
}

/**
 * Takes the handoff parameters back out of the address bar.
 *
 * `replaceState` rather than a navigation, so the entry the user would go back to
 * does not still contain a refresh token.
 */
function stripHandoffParams(url: URL): void {
  const hadAny = SESSION_HANDOFF_PARAMS.some((key) => url.searchParams.has(key))
  if (!hadAny) return

  for (const key of SESSION_HANDOFF_PARAMS) {
    url.searchParams.delete(key)
  }
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  )
}

/**
 * Removes the handoff parameters from an arbitrary url, keeping it relative.
 *
 * Used when recording where somebody was headed before being sent to sign in.
 */
export function withoutHandoffParams(url: string): string {
  // The base only lets a relative url parse; the host is dropped either way.
  const parsed = new URL(url, window.location.origin)
  for (const key of SESSION_HANDOFF_PARAMS) {
    parsed.searchParams.delete(key)
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

/**
 * Signs out here and at the identity provider.
 *
 * Deliberately does not route to `/login`. The realm may still hold a single
 * sign-on session, which would answer a sign-in page with a fresh one without
 * anybody being asked -- and "Sign out" would read as having done nothing at all.
 * The local teardown happens first and unconditionally, so a realm that cannot be
 * reached still leaves the browser signed out here.
 */
export function signOut(): void {
  const { refreshToken } = getSession()
  if (refreshToken !== null) {
    blacklistRefreshToken(refreshToken)
  }
  endSessionLocally()
  window.location.href = signOutUrl()
}
